import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowLeft, Newspaper, Eye, EyeOff, ImagePlus, Pin, Bell, Clock3, X } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });
const CATEGORIES = ["Gold", "Crypto", "Market", "Platform"];

export const newsCover = (n) => (n.cover_path ? `${BASE}/api/files/${n.cover_path}` : n.cover_url || "");
const fmtDate = (d) => {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" });
};

function NewsCard({ n, big = false }) {
  const cover = newsCover(n);
  return (
    <Link to={`/news/${n.news_id}`} className={big ? "news-card big" : "news-card"} data-testid={`news-card-${n.news_id}`}>
      <div className="news-cover">
        {cover ? <img src={cover} alt="" loading="lazy" /> : <Newspaper size={28} />}
        <span className={`news-cat cat-${n.category?.toLowerCase()}`}>{n.category}</span>
        {n.featured && <span className="news-featured" data-testid={`news-featured-badge-${n.news_id}`}><Pin size={10} /> Featured</span>}
      </div>
      <div className="news-body">
        <small>{fmtDate(n.publish_date)}</small>
        <h3>{n.title}</h3>
        <p>{n.summary}</p>
        <span className="news-read">Read more <ArrowUpRight size={13} /></span>
      </div>
    </Link>
  );
}

export function NewsSection() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/news", { params: { limit: 4 } }).then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="section news-section" data-testid="home-news-section">
      <div className="section-heading">
        <div>
          <div className="eyebrow">MARKET DESK</div>
          <h2>Gold & market<br />news, in brief.</h2>
        </div>
        <Link className="text-link" data-testid="home-news-view-all" to="/news">View all news <ArrowUpRight size={15} /></Link>
      </div>
      <div className="news-grid home">
        {items.map((n, i) => <NewsCard key={n.news_id} n={n} big={i === 0} />)}
      </div>
    </section>
  );
}

function NewsBrowser() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  useEffect(() => {
    api.get("/news", { params: { limit: 200, category: cat } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [cat]);
  return (
    <>
      <div className="news-filters">
        {["", ...CATEGORIES].map((c) => (
          <button key={c || "all"} className={cat === c ? "news-filter on" : "news-filter"} onClick={() => setCat(c)}
            data-testid={`news-filter-${(c || "all").toLowerCase()}`}>{c || "All"}</button>
        ))}
      </div>
      {items === null ? <div className="empty-state">Loading news…</div>
        : items.length === 0 ? <div className="empty-state" data-testid="news-empty">No news in this category yet.</div>
        : <div className="news-grid">{items.map((n) => <NewsCard key={n.news_id} n={n} />)}</div>}
    </>
  );
}

export function NewsPage() {
  return (
    <main className="simple-page news-page" data-testid="news-page">
      <div className="eyebrow">MARKET DESK</div>
      <h1>News that<br /><em>moves markets.</em></h1>
      <p className="lead">Gold, crypto and market updates curated by the Nexa Pro desk. Information only, not financial advice.</p>
      <NewsBrowser />
    </main>
  );
}

export function DashboardNews() {
  return (
    <section className="panel wide dash-news" data-testid="dashboard-news-panel">
      <div className="panel-head">
        <div><div className="eyebrow">MARKET DESK</div><h2>Latest gold & market news</h2></div>
        <Link to="/news" className="text-link" data-testid="dashboard-news-open-page">Open news page <ArrowUpRight size={14} /></Link>
      </div>
      <NewsBrowser />
    </section>
  );
}

export function NewsDetail() {
  const { newsId } = useParams();
  const [n, setN] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/news/${newsId}`).then((r) => setN(r.data)).catch(() => setErr(true)); }, [newsId]);
  if (err) return <main className="simple-page"><div className="empty-state" data-testid="news-not-found">News not found. <Link to="/news" className="text-link">Back to news</Link></div></main>;
  if (!n) return <main className="simple-page"><div className="empty-state">Loading…</div></main>;
  const cover = newsCover(n);
  return (
    <main className="simple-page news-detail" data-testid="news-detail">
      <Link to="/news" className="back-link" data-testid="news-back-link"><ArrowLeft size={13} /> All news</Link>
      <div className="news-detail-meta"><span className={`news-cat cat-${n.category?.toLowerCase()}`}>{n.category}</span><small>{fmtDate(n.publish_date)}</small></div>
      <h1 data-testid="news-detail-title">{n.title}</h1>
      <p className="lead">{n.summary}</p>
      {cover && <img className="news-detail-cover" src={cover} alt="" />}
      <div className="news-content" data-testid="news-detail-content">
        {(n.content || "").split(/\n+/).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </main>
  );
}

const emptyNews = { title: "", summary: "", content: "", category: "Gold", published: true, publish_date: new Date().toISOString().slice(0, 10), cover_url: "" };

function NewsCoverUpload({ item, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", f);
    api.post(`/admin/news/${item.news_id}/cover`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then(onDone).catch((er) => window.alert(er.response?.data?.detail || "Upload failed"))
      .finally(() => { setBusy(false); if (ref.current) ref.current.value = ""; });
  };
  const cover = newsCover(item);
  return (
    <div className="news-admin-cover">
      {cover ? <img src={cover} alt="" /> : <span><Newspaper size={16} /></span>}
      <button type="button" className="mini-button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`news-cover-upload-${item.news_id}`}>
        <ImagePlus size={11} /> {busy ? "Uploading…" : "Cover"}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} data-testid={`news-cover-input-${item.news_id}`} />
    </div>
  );
}

function NewsForm({ editing, setEditing, onSaved }) {
  const [err, setErr] = useState("");
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault(); setErr("");
    const payload = { title: editing.title, summary: editing.summary, content: editing.content, category: editing.category,
      published: editing.published, publish_date: editing.publish_date, cover_url: editing.cover_url || null,
      publish_in_hours: Number(editing.publish_in_hours) > 0 ? Number(editing.publish_in_hours) : null };
    try {
      if (editing.news_id) await api.put(`/admin/news/${editing.news_id}`, payload);
      else await api.post("/admin/news", payload);
      onSaved();
    } catch (er) { setErr(er.response?.data?.detail?.toString() || "Save failed"); }
  };
  return (
    <form className="panel edit-panel" onSubmit={save} data-testid="news-form">
      <h3>{editing.news_id ? "Edit news" : "New news post"}</h3>
      {err && <div className="error-box" data-testid="news-form-error">{err}</div>}
      <div className="grid-two">
        <label>Title<input required minLength={3} value={editing.title} onChange={set("title")} data-testid="news-title-input" /></label>
        <label>Category
          <select value={editing.category} onChange={set("category")} data-testid="news-category-input">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Publish date<input type="date" required value={editing.publish_date || ""} onChange={set("publish_date")} disabled={Number(editing.publish_in_hours) > 0}
          title={Number(editing.publish_in_hours) > 0 ? "Set automatically to the day it goes live" : ""} data-testid="news-date-input" /></label>
        <label>Cover image URL (optional)<input value={editing.cover_url || ""} onChange={set("cover_url")} placeholder="https://…" data-testid="news-cover-url-input" /></label>
        <label>Publish after (hours) — leave empty to publish now
          <input type="number" min="0" step="0.25" value={editing.publish_in_hours ?? ""} onChange={set("publish_in_hours")} placeholder="e.g. 3" data-testid="news-schedule-hours-input" />
        </label>
      </div>
      <label className="news-wide-label">Short summary<textarea required minLength={3} rows={2} value={editing.summary} onChange={set("summary")} data-testid="news-summary-input" /></label>
      <label className="news-wide-label">Full content<textarea required minLength={3} rows={8} value={editing.content} onChange={set("content")} data-testid="news-content-input" /></label>
      <label className="checkbox news-check">
        <input type="checkbox" checked={editing.published && !(Number(editing.publish_in_hours) > 0)} disabled={Number(editing.publish_in_hours) > 0}
          onChange={set("published")} data-testid="news-published-input" /> {Number(editing.publish_in_hours) > 0 ? `Will auto-publish in ${editing.publish_in_hours}h and alert all users` : "Published (visible on website)"}
      </label>
      <p className="panel-copy">Publishing a post for the first time sends a notification to all users. Save the post first, then use the "Cover" button in the list to upload an image.</p>
      <div className="button-row">
        <button className="button primary" data-testid="news-save-button">Save</button>
        <button type="button" className="button ghost" onClick={() => setEditing(null)} data-testid="news-cancel-button">Cancel</button>
      </div>
    </form>
  );
}

export function AdminNews() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Delete this news post?")) { await api.delete(`/admin/news/${id}`); load(); } };
  const toggle = async (id) => { await api.post(`/admin/news/${id}/toggle`); load(); };
  const feature = async (id) => { await api.post(`/admin/news/${id}/feature`); load(); };
  return (
    <section className="panel wide" data-testid="admin-news-panel">
      <div className="panel-head">
        <div><div className="eyebrow">NEWS</div><h2>News posts ({items.length})</h2></div>
        <button className="button primary" onClick={() => setEditing({ ...emptyNews })} data-testid="news-new-button"><ArrowUpRight size={14} /> New post</button>
      </div>
      {editing && <NewsForm editing={editing} setEditing={setEditing} onSaved={() => { setEditing(null); load(); }} />}
      <div className="table-scroll">
        <table className="data-table">
          <thead><tr><th>Cover</th><th>Title</th><th>Category</th><th>Date</th><th>Status</th><th /></tr></thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.news_id} data-testid={`admin-news-row-${n.news_id}`}>
                <td><NewsCoverUpload item={n} onDone={load} /></td>
                <td className="news-admin-title"><b>{n.featured && <span className="news-pin-tag" data-testid={`news-admin-featured-${n.news_id}`}><Pin size={10} /> Featured</span>}{n.title}</b><small>{n.summary}</small></td>
                <td>{n.category}</td>
                <td>{fmtDate(n.publish_date)}</td>
                <td>
                  <span className={`status ${n.published ? "active" : n.scheduled_at ? "scheduled" : "paused"}`} data-testid={`news-status-${n.news_id}`}>
                    {n.published ? "Published" : n.scheduled_at ? <><Clock3 size={10} /> Scheduled</> : "Hidden"}
                  </span>
                  {!n.published && n.scheduled_at && <small className="news-sched-at" data-testid={`news-scheduled-at-${n.news_id}`}>{new Date(n.scheduled_at).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</small>}
                </td>
                <td className="news-actions">
                  <button className="mini-button" onClick={() => toggle(n.news_id)} data-testid={`news-toggle-${n.news_id}`}>
                    {n.published ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Publish</>}
                  </button>
                  <button className={n.featured ? "mini-button pinned" : "mini-button"} onClick={() => feature(n.news_id)} data-testid={`news-feature-${n.news_id}`}>
                    <Pin size={11} /> {n.featured ? "Unpin" : "Pin to top"}
                  </button>
                  <button className="mini-button" onClick={() => { setEditing({ ...n, cover_url: n.cover_url || "", publish_in_hours: !n.published && n.scheduled_at ? Math.max(0.25, Math.round((new Date(n.scheduled_at) - Date.now()) / 36e5 * 4) / 4) : "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`news-edit-${n.news_id}`}>Edit</button>
                  <button className="mini-button danger" onClick={() => del(n.news_id)} data-testid={`news-delete-${n.news_id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const unread = items.filter((x) => !x.read).length;
  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    if (n.news_id) { setOpen(false); nav(`/news/${n.news_id}`); } else load();
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="notif-wrap">
      <button className="notif-bell" onClick={() => setOpen(!open)} data-testid="notification-bell" aria-label="Notifications">
        <Bell size={17} />{unread > 0 && <span className="notif-count" data-testid="notification-unread-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel" data-testid="notification-panel">
          <div className="notif-head"><b>Notifications</b>
            <span className="notif-head-actions">
              {unread > 0 && <button className="mini-button" onClick={readAll} data-testid="notification-read-all">Mark all read</button>}
              <button className="notif-close" onClick={() => setOpen(false)} aria-label="Close notifications" data-testid="notification-close"><X size={16} /></button>
            </span>
          </div>
          <div className="notif-list">
            {items.length === 0 && <div className="empty-state">No notifications yet.</div>}
            {items.map((n) => (
              <button key={n.notification_id} className={`notif-item ${n.read ? "" : "unread"} ${n.news_id ? "link" : ""}`} onClick={() => openItem(n)}
                data-testid={`notification-item-${n.notification_id}`}>
                {n.news_id && <Newspaper size={14} className="notif-ico" />}
                <span><b>{n.title}</b><small>{n.body}</small><em>{new Date(n.created_at).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}{n.news_id && " · Read news →"}</em></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
